import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Form } from '@arco-design/web-react';

import { IconFont } from '@/components';
import { LargeSelect } from '@/components/LargeSelect';
import { VolumeOptionLabel } from '@/components/Workload/storage-settings';

import { useExistingVolumeOptions } from './hooks/useExistingVolumeOptions';
import styles from './index.module.less';
import type { ExistingVolumeSelectFieldProps } from './types';

/** 已有云盘选择表单项：LargeSelect + VolumeOptionLabel */
export function ExistingVolumeSelectField({
  form: _form,
  field,
  volumeType,
  namespace,
  enabled = true,
  getPopupContainer,
  formItemClassName,
  label,
  requiredMessage,
  onVolumeSelect,
}: ExistingVolumeSelectFieldProps) {
  const { t } = useTranslation();
  const { options, isLoading } = useExistingVolumeOptions({ namespace, volumeType, enabled });

  const selectOptions = useMemo(
    () =>
      options.map((item) => ({
        value: item.value,
        label: (
          <VolumeOptionLabel
            iconfontName="icon-yunpan"
            value={item.value}
            label={item.label}
            extraItems={[
              item.storageClass
                ? {
                    label: t('common:storage.class'),
                    value: item.storageClass,
                    frozenWidth: 240,
                  }
                : null,
              item.size
                ? {
                    label: t('common:capacity'),
                    value: item.size,
                    frozenWidth: true,
                  }
                : null,
            ].filter(Boolean)}
          />
        ),
      })),
    [options, t],
  );

  const placeholderNode = useMemo(
    () => (
      <div className={styles.selectPlaceholder}>
        <IconFont name="icon-yunpan" style={{ fontSize: 36, opacity: 0.45 }} />
        <span>{t('common:select.placeholder')}</span>
      </div>
    ),
    [t],
  );

  const handleSelectChange = useCallback(
    (value: string | undefined) => {
      const selected = value ? options.find((item) => item.value === value) : undefined;
      onVolumeSelect?.(selected);
    },
    [onVolumeSelect, options],
  );

  return (
    <Form.Item
      field={field}
      label={label ?? t('components:cloud.disk')}
      rules={[{ required: true, message: requiredMessage ?? t('common:validate.not-empty') }]}
      className={formItemClassName}
    >
      <LargeSelect
        showSearch
        loading={isLoading}
        options={selectOptions}
        placeholder=""
        placeholderNode={placeholderNode}
        onChange={handleSelectChange}
        getPopupContainer={getPopupContainer}
      />
    </Form.Item>
  );
}
